// The language EmmA speaks with this customer (Jacky, 14 Oct 2026: "Emma also need to have ability to speak using
// language set by Artemis"). Set here, used by EmmA on the customer's Proposal and Job pages; the pages stay English.
// The list is the bb.config document customer_languages. One change is one set_customer_language action.
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, authHeaders, client, fetchImpl } from "../lib/api";
import { env } from "../lib/env";
import { useT } from "../lib/i18n";
import { selectCls } from "./job-bits";

type LanguageRead = { language: string; set_by: string | null; updated_at: string | null; options: { code: string; name: string }[] };

export function CustomerLanguage({ contactId }: { contactId: string }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false), [notice, setNotice] = useState("");
  const q = useQuery({ queryKey: ["contact", contactId, "language"], enabled: !!contactId && !env.mock, queryFn: async () => {
    const r = await fetchImpl(`${env.apiBase}/api/contacts/${encodeURIComponent(contactId)}/language`, { headers: await authHeaders() });
    const b = await r.json() as { data?: LanguageRead; error?: string };
    if (!r.ok || !b.data) throw new Error(b.error ?? "Language unavailable");
    return b.data;
  } });
  if (env.mock || q.isError) return null;
  const d = q.data;
  async function change(language: string) {
    if (!d || language === d.language) return;
    setBusy(true); setNotice("");
    try {
      const r = await api.propose(client, { command: "set_customer_language", args: { contact_id: contactId, language }, idempotency_key: crypto.randomUUID() });
      setNotice(r.status === "executed" ? t("custLang.saved") : r.status === "pending" ? t("custLang.pending") : t("custLang.failed", { reason: r.reason }));
      await qc.invalidateQueries({ queryKey: ["contact", contactId, "language"] });
    } catch (e) { setNotice(t("custLang.failed", { reason: (e as Error).message })); } finally { setBusy(false); }
  }
  return (
    <section className="my-3 flex flex-wrap items-center gap-2 text-sm">
      <label className="flex items-center gap-2"><span className="text-muted-foreground">{t("custLang.label")}</span>
        <select className={selectCls} value={d?.language ?? "en"} disabled={!d || busy} onChange={(e) => void change(e.target.value)}>
          {(d?.options ?? [{ code: "en", name: "English" }]).map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
        </select>
      </label>
      <span className="text-micro text-muted-foreground">{notice || t("custLang.hint")}</span>
    </section>
  );
}
